import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaypointService } from '../../../services/paypoint.service';
import { PaypointRequest, PSPConfig } from '../../../models/paypoint-request.interface';

@Component({
  selector: 'kt-bridgepay-add-paypoint',
  templateUrl: './add-paypoint.component.html',
  styleUrls: ['./add-paypoint.component.scss'],
})
export class AddPaypointComponent implements OnInit {
  @Input() organization: string;
  @Input() officeId: string;

  @Output() addPaypoint = new EventEmitter<void>();

  addPaypointForm: FormGroup;
  providerOptions = [
    { value: '', viewValue: 'Select provider' },
    { value: 'orientalbank', viewValue: 'Oriental Bank' },
    { value: 'stripe', viewValue: 'Stripe' },
  ];
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private paypointService: PaypointService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    // Initialize form – note: paypointCode is auto-generated on backend so we disable its input.
    this.addPaypointForm = this.fb.group({
      paypointCode: [{ value: '', disabled: true }],
      provider: ['', Validators.required],
      officeId: ['office_123'], // Office ID comes from the parent context
      // Oriental Bank config
      storeId: [''],
      storeKey: [''],
      entityId: [''],
      locationId: [''],
      // Stripe config
      apiKey: [''],
      publishableKey: [''],
      webhookSecret: [''],
    });

    // Adjust validators dynamically based on provider selection.
    this.addPaypointForm.get('provider').valueChanges.subscribe((provider) => {
      if (provider === 'orientalbank') {
        this.setOrientalBankValidators();
        this.clearStripeValidators();
      } else if (provider === 'stripe') {
        this.setStripeValidators();
        this.clearOrientalBankValidators();
      } else {
        this.clearOrientalBankValidators();
        this.clearStripeValidators();
      }
    });
  }

  private setOrientalBankValidators() {
    this.addPaypointForm.get('storeId').setValidators(Validators.required);
    this.addPaypointForm.get('storeKey').setValidators(Validators.required);
    this.addPaypointForm.get('entityId').setValidators(Validators.required);
    this.addPaypointForm.get('locationId').setValidators(Validators.required);
    this.updateOrientalBankValidity();
  }

  private clearOrientalBankValidators() {
    this.addPaypointForm.get('storeId').clearValidators();
    this.addPaypointForm.get('storeKey').clearValidators();
    this.addPaypointForm.get('entityId').clearValidators();
    this.addPaypointForm.get('locationId').clearValidators();
    this.updateOrientalBankValidity();
  }

  private updateOrientalBankValidity() {
    this.addPaypointForm.get('storeId').updateValueAndValidity();
    this.addPaypointForm.get('storeKey').updateValueAndValidity();
    this.addPaypointForm.get('entityId').updateValueAndValidity();
    this.addPaypointForm.get('locationId').updateValueAndValidity();
  }

  private setStripeValidators() {
    this.addPaypointForm.get('apiKey').setValidators(Validators.required);
    this.addPaypointForm.get('publishableKey').setValidators(Validators.required);
    this.addPaypointForm.get('webhookSecret').setValidators(Validators.required);
    this.updateStripeValidity();
  }

  private clearStripeValidators() {
    this.addPaypointForm.get('apiKey').clearValidators();
    this.addPaypointForm.get('publishableKey').clearValidators();
    this.addPaypointForm.get('webhookSecret').clearValidators();
    this.updateStripeValidity();
  }

  private updateStripeValidity() {
    this.addPaypointForm.get('apiKey').updateValueAndValidity();
    this.addPaypointForm.get('publishableKey').updateValueAndValidity();
    this.addPaypointForm.get('webhookSecret').updateValueAndValidity();
  }

  onVerifyCredentials(): void {
    if (this.addPaypointForm.invalid) {
      this.snackBar.open('Please fill in all required fields.', 'Close', { duration: 3000 });
      return;
    }

    const pspConfig = this.buildPSPConfig();

    const paypointData: PaypointRequest = {
      office_id: this.officeId,
      provider: this.addPaypointForm.get('provider').value,
      psp_config: pspConfig,
    };
    this.paypointService.verifyCredentials(this.organization, paypointData).subscribe(
      () => {
        this.snackBar.open('Credentials verified successfully!', 'Close', { duration: 3000 });
      },
      (error) => {
        this.snackBar.open('Verification failed: ' + error.message, 'Close', { duration: 5000 });
      },
    );
  }

  private buildPSPConfig(): PSPConfig {
    if (this.addPaypointForm.get('provider').value === 'orientalbank') {
      return {
        store_id: this.addPaypointForm.get('storeId').value,
        store_key: this.addPaypointForm.get('storeKey').value,
        entity_id: this.addPaypointForm.get('entityId').value,
        location_id: this.addPaypointForm.get('locationId').value,
      };
    } else if (this.addPaypointForm.get('provider').value === 'stripe') {
      return {
        api_key: this.addPaypointForm.get('apiKey').value,
        publishable_key: this.addPaypointForm.get('publishableKey').value,
        webhook_secret: this.addPaypointForm.get('webhookSecret').value,
      };
    }
    return {};
  }

  onSave(): void {
    if (this.addPaypointForm.invalid) {
      this.snackBar.open('Please fill all required fields correctly.', 'Close', { duration: 3000 });
      return;
    }
    this.isSubmitting = true;
    const pspConfig = this.buildPSPConfig();

    const paypointData: PaypointRequest = {
      office_id: this.officeId,
      provider: this.addPaypointForm.get('provider').value,
      psp_config: pspConfig,
    };

    this.paypointService.addPaypoint(this.organization, paypointData).subscribe(
      () => {
        this.snackBar.open('Paypoint added successfully!', 'Close', { duration: 3000 });
        this.isSubmitting = false;
        this.addPaypointForm.reset({ officeId: 'office_123', provider: '' });
        this.addPaypoint.emit();
      },
      (error) => {
        this.snackBar.open('Error adding paypoint: ' + error.message, 'Close', { duration: 5000 });
        this.isSubmitting = false;
      },
    );
  }
}
